import * as React from "react";
import { createStyles, Link, makeStyles, Typography } from "@material-ui/core";

import { CONTACT_US_FORM_URL } from "constants/index";
import { ErrorInfo } from "services/royaltyAccount/types";
import { RoyaltyPayor } from "services/royaltyPayor/types";
import { linkPurpleStyles } from "theme/constants";

const useStyles = makeStyles(() =>
  createStyles({
    link: linkPurpleStyles,
    paragraph: {
      fontSize: "inherit",
      "&:last-child": {
        marginBottom: 0,
      },
    },
  }),
);

interface ConnectAccountErrorMessageProps {
  errorInfo: ErrorInfo;
  royaltyPayor: RoyaltyPayor;
  hasConnectedBefore?: boolean;
}

const ConnectAccountErrorMessage: React.FC<ConnectAccountErrorMessageProps> = ({
  errorInfo,
  royaltyPayor,
  hasConnectedBefore,
}) => {
  const classes = useStyles();

  const stringToParagraphs = (message: string): React.ReactNode => {
    return message.split("\n").map((x, i) => (
      <Typography key={i} className={classes.paragraph}>
        {x}
      </Typography>
    ));
  };

  switch (errorInfo.errorType) {
    case "login":
      return (
        <>
          {stringToParagraphs(
            "Whoops! Your login information wasn’t correct.\nCheck your username and password and try again.",
          )}
        </>
      );
    case "invalidProLogin":
      if (hasConnectedBefore) {
        return (
          <>
            {stringToParagraphs(
              royaltyPayor.username_or_email === "email"
                ? `We weren’t able to connect to ${royaltyPayor.display_name} using that email and password.`
                : `We weren’t able to connect to ${royaltyPayor.display_name} using that username and password.`,
            )}
            {stringToParagraphs(
              "You may need to update your credentials on the seller dashboard.",
            )}
          </>
        );
      }
      return (
        <>
          {stringToParagraphs(
            royaltyPayor.username_or_email === "email"
              ? `We weren’t able to connect to ${royaltyPayor.display_name} using that email and password.`
              : `We weren’t able to connect to ${royaltyPayor.display_name} using that username and password.`,
          )}
          <Typography className={classes.paragraph}>
            Make sure you’re using the right login information, or{" "}
            <Link
              href={royaltyPayor.portal_url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              click here to reset your info on the {royaltyPayor.display_name}{" "}
              site.
            </Link>
          </Typography>
        </>
      );
    case "connection":
      return (
        <>
          {stringToParagraphs(
            "We had a problem connecting to the server.\nPlease check your internet connection or contact support.",
          )}
        </>
      );
    case "email":
      return (
        <>
          {stringToParagraphs(
            "It looks like you are trying to use a BMI account.",
          )}
          <Typography className={classes.paragraph}>
            You may need to{" "}
            <Link
              href={royaltyPayor.portal_url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              confirm your email address with BMI
            </Link>{" "}
            before continuing.
          </Typography>
        </>
      );
    case "invalidInput":
      return <>{stringToParagraphs(`Invalid input!\n ${errorInfo.message}`)}</>;
    case "invalid2FASubmitted":
      return (
        <>
          {stringToParagraphs(
            "The two-factor authentication code you entered is incorrect. Please check the code and try again.",
          )}
        </>
      );
    case "accountLocked":
    case "passwordExpired":
      return (
        <>
          {stringToParagraphs(
            `It looks like you are trying to use a ${royaltyPayor.display_name} account.`,
          )}
          <Typography className={classes.paragraph}>
            You may need to{" "}
            <Link
              href={royaltyPayor.portal_url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              reset your password
            </Link>{" "}
            before continuing.
          </Typography>
        </>
      );
    case "passwordNeedsUpdate":
      return (
        <>
          {stringToParagraphs("We were unable to connect to your account.")}
          <Typography className={classes.paragraph}>
            Please{" "}
            <Link
              href={royaltyPayor.portal_url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              log in to the {royaltyPayor.display_name} portal
            </Link>{" "}
            and update your password.
          </Typography>
          {stringToParagraphs(
            "Then retry connecting your account with the new password.",
          )}
        </>
      );
    case "noStatements":
      return (
        <Typography className={classes.paragraph}>
          We couldn&apos;t find any royalty statements in your{" "}
          {royaltyPayor.display_name} account.
        </Typography>
      );
    case "portalChanged":
      return (
        <>
          {stringToParagraphs("We were unable to connect to your account.")}
          {stringToParagraphs(
            "This may be caused by a temporary outage. Please try again later.",
          )}
        </>
      );
    case "other":
      // Handling of the error returned by ValidateRoyaltyAccountCredential._post @orderbook/views.py
      if (errorInfo.message === "No sub-accounts with USD as currency") {
        return (
          <>
            {stringToParagraphs(
              "No sub-accounts found with currency set to USD. Please contact us for a custom valuation.",
            )}
          </>
        );
      }

      return (
        <>
          {stringToParagraphs("We were unable to connect to your account.")}
          <Typography className={classes.paragraph}>
            This may be caused by a temporary outage. Please try again in a few
            minutes. If the problem persists,{" "}
            <Link href={CONTACT_US_FORM_URL} className={classes.link}>
              click here
            </Link>{" "}
            to contact us.
          </Typography>
        </>
      );
  }
};

export default ConnectAccountErrorMessage;
